import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck, ShieldOff, RefreshCw, Search, Trash2, Loader2,
  AlertTriangle, Globe, X, ChevronRight, Activity, Clock,
  Sparkles, Check, Filter,
} from 'lucide-react';
import { Card } from '../components/Card';
import { SectionHeader } from '../components/Tabs';
import { Badge, Dot } from '../components/Badge';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { EmptyState, Spinner, Dialog } from '../components/Dialog';
import { OperatorStat } from '../components/OperatorStat';
import { adminFetch } from '../adminApi';
import { cn, formatNumber, formatRelative } from '../lib/utils';

/**
 * CFDomainsPage — operator-side view of the dynamic router's stealth cache.
 *
 * Every domain here will skip the cheap-path probe and go straight to
 * Datames on future requests. Operators can review the list, search it,
 * remove entries that were added by mistake, and see the cache hit count.
 *
 * Phase-20 redesign: this is the operator UI for the on-disk
 * `cf_domains.json` store the v1 router reads.
 */
export default function CFDomainsPage() {
  const [domains, setDomains] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // domain string
  const [busyDelete, setBusyDelete] = useState(false);
  const [filterKind, setFilterKind] = useState('all');

  async function load() {
    setLoading(true); setError(null);
    try {
      const data = await adminFetch('/admin/cf-domains');
      setDomains(data.domains || {});
    } catch (e) {
      setError(e.message || 'failed_to_load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const entries = useMemo(() => {
    return Object.entries(domains).map(([d, info]) => ({ domain: d, ...info }));
  }, [domains]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return entries.filter((e) => {
      if (needle && !e.domain.includes(needle)) return false;
      if (filterKind !== 'all' && e.challenge_kind !== filterKind) return false;
      return true;
    }).sort((a, b) => (b.last_seen || '').localeCompare(a.last_seen || ''));
  }, [entries, search, filterKind]);

  const totalCount = entries.length;
  const highConf = entries.filter((e) => (e.confidence || 0) >= 0.8).length;
  const totalHits = entries.reduce((s, e) => s + (e.hit_count || 0), 0);
  const recentAdds = entries.filter((e) => {
    if (!e.first_seen) return false;
    const ageDays = (Date.now() - new Date(e.first_seen).getTime()) / 86400000;
    return ageDays < 7;
  }).length;

  async function doDelete(domain) {
    setBusyDelete(true);
    try {
      await adminFetch(`/admin/cf-domains/${encodeURIComponent(domain)}`, { method: 'DELETE' });
      setConfirmDelete(null);
      await load();
    } catch (e) {
      alert(`Could not remove: ${e.message}`);
    } finally {
      setBusyDelete(false);
    }
  }

  return (
    <div className="space-y-6">
      <SectionHeader
        title="CF domains"
        description="Domains the dynamic router has learned to escalate straight to Datames. Remove entries to force the cheap probe again."
        actions={
          <Button variant="secondary" size="sm" onClick={load}>
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        }
      />

      {/* Top stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <OperatorStat
          icon={ShieldCheck}
          label="Tracked domains"
          value={loading ? null : formatNumber(totalCount)}
          hint={loading ? 'Loading…' : 'Will skip cheap probe'}
          tone="warning"
          loading={loading}
        />
        <OperatorStat
          icon={Sparkles}
          label="High confidence"
          value={loading ? null : formatNumber(highConf)}
          hint={loading ? 'Loading…' : 'confidence ≥ 80%'}
          tone={highConf > 0 ? 'destructive' : 'success'}
          loading={loading}
        />
        <OperatorStat
          icon={Activity}
          label="Cache hits"
          value={loading ? null : formatNumber(totalHits)}
          hint={loading ? 'Loading…' : 'Times these were used to skip a probe'}
          tone="brand"
          loading={loading}
        />
        <OperatorStat
          icon={Clock}
          label="Added (7d)"
          value={loading ? null : formatNumber(recentAdds)}
          hint={loading ? 'Loading…' : 'Newly escalated this week'}
          tone="neutral"
          loading={loading}
        />
      </div>

      {/* Filters */}
      <Card>
        <Card.Body className="p-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-fg-subtle pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by domain…"
                className="pl-9"
              />
            </div>
            <select
              value={filterKind}
              onChange={(e) => setFilterKind(e.target.value)}
              className="h-9 px-3 text-sm bg-bg border border-border rounded-md text-fg focus:outline-none focus:border-brand"
            >
              <option value="all">All challenge types</option>
              <option value="cloudflare">Cloudflare</option>
              <option value="turnstile">Turnstile</option>
              <option value="hcaptcha">hCaptcha</option>
            </select>
            <Badge variant="default">{filtered.length} / {totalCount}</Badge>
          </div>
        </Card.Body>
      </Card>

      {/* List */}
      <Card>
        <Card.Header>
          <div>
            <Card.Title>Cache entries</Card.Title>
            <Card.Subtitle>Newest entries first. Each row is a domain the router has learned.</Card.Subtitle>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error ? (
            <div className="p-6 text-sm text-destructive flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          ) : loading ? (
            <div className="p-8 flex items-center justify-center"><Spinner size={20} /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8">
              <EmptyState
                icon={ShieldCheck}
                title={totalCount === 0 ? 'No CF domains tracked' : 'No matches'}
                description={
                  totalCount === 0
                    ? 'When the dynamic router detects a high-confidence Cloudflare challenge, the domain is added here automatically.'
                    : 'Try a different search or filter.'
                }
              />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {filtered.map((e) => (
                <DomainRow key={e.domain} entry={e} onRemove={() => setConfirmDelete(e.domain)} />
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>

      {/* Help blurb */}
      <Card>
        <Card.Body className="px-5 py-4 text-sm text-fg-muted flex items-start gap-3">
          <ShieldOff className="h-4 w-4 text-fg-subtle mt-0.5 shrink-0" />
          <div>
            <strong className="text-fg">Why a domain is on this list:</strong> the dynamic router
            detected a high-confidence Cloudflare challenge on it (confidence ≥ 0.8).
            Future requests skip the cheap probe and go straight to Datames so the call
            doesn't waste 2 seconds trying tls_client first. Removing a domain here
            forces the cheap probe again the next time it's called.
          </div>
        </Card.Body>
      </Card>

      <ConfirmDeleteDialog
        domain={confirmDelete}
        busy={busyDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => doDelete(confirmDelete)}
      />
    </div>
  );
}

function DomainRow({ entry, onRemove }) {
  const conf = entry.confidence || 0;
  const confColor = conf >= 0.95 ? 'text-destructive'
                  : conf >= 0.8  ? 'text-warning'
                  : 'text-fg-muted';
  const kindColor = {
    cloudflare: 'warning',
    turnstile:  'info',
    hcaptcha:   'destructive',
  }[entry.challenge_kind] || 'default';

  return (
    <li className="flex items-center gap-4 px-5 py-3 hover:bg-surface-2/40 group">
      <div className="h-9 w-9 rounded-lg bg-surface-2 flex items-center justify-center shrink-0">
        <Globe className="h-4 w-4 text-fg-muted" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-mono text-fg truncate">{entry.domain}</span>
          <Badge variant={kindColor} className="text-2xs">{entry.challenge_kind || 'unknown'}</Badge>
        </div>
        <div className="text-2xs text-fg-muted mt-0.5 flex items-center gap-2 flex-wrap">
          <span className={cn('tabular-nums', confColor)}>
            {Math.round(conf * 100)}% conf
          </span>
          <span>·</span>
          <span>{formatNumber(entry.hit_count || 0)} hits</span>
          <span>·</span>
          <span>first {entry.first_seen ? formatRelative(entry.first_seen) : '—'}</span>
          <span>·</span>
          <span>last {entry.last_seen ? formatRelative(entry.last_seen) : '—'}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="text-2xs text-destructive hover:bg-destructive/10 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1"
      >
        <Trash2 className="h-3 w-3" />
        Remove
      </button>
    </li>
  );
}

function ConfirmDeleteDialog({ domain, busy, onClose, onConfirm }) {
  return (
    <Dialog
      open={!!domain}
      onClose={onClose}
      title="Remove from cache?"
      description={`Future requests to ${domain || ''} will go through the cheap probe again. The domain may be re-added if it triggers a real challenge.`}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Remove
          </Button>
        </div>
      }
    >
      <div className="px-3 py-2 rounded-md bg-warning/10 border border-warning/30 text-xs text-warning flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>This won't break anything — it just means the next request to this domain will cost 1 credit on the cheap probe before being escalated (4 credits) if a challenge is detected.</span>
      </div>
    </Dialog>
  );
}
